import { GridProgress, TableNoDataRow } from '@/common/components'
import CustomPagination from '@/common/components/CustomPagination/CustomPagination'
import { usePagination } from '@/common/components/CustomPagination/hooks/usePagination'
import { loadViews } from '@/hooks'
import { MenusNavItem } from '@/menus'
import { useCourseCatalogPrefetch as usePrefetch } from '@/services/api/tenant/courseCatalogEnhanced'
import CustomPalette from '@/theme/CustomPalette'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchCourses } from '../../hooks/useSearchCourses'
import { Course } from '../../types'
import { CourseListItem } from '../CourseListItem/CourseListItem'

export type DataListProps = {
  routeLabel: string
  titles: string[]
  emptyList: string
  selectedCourse?: Course
  menus?: MenusNavItem[]
  paginationDisabledFromSearch?: boolean
  openAction: (courseCode: string) => void
  closeAction?: () => void
}

export const CourseList = (props: DataListProps): JSX.Element => {
  const { page, size, handlePageChange, handleLimitChange } = usePagination()
  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchCourses(page, size)

  const isLoading = isFetching || isUninitialized

  const [Menus, setMenus] = useState<Array<JSX.Element>>([])

  useEffect(() => {
    loadViews({
      views: props.menus,
      action: props.closeAction,
      ...props.selectedCourse,
    }).then(setMenus)
  }, [props.menus])

  const prefetchPage = usePrefetch('listCourses')

  const prefetchNext = useCallback(() => {
    prefetchPage({ page: page + 1, size })
  }, [prefetchPage, page, size])

  const [delayedLoading, setDelayedLoading] = useState(true)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (isLoading) {
      setDelayedLoading(true)
    } else {
      timer = setTimeout(() => {
        setDelayedLoading(false)
      }, 500)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isLoading])

  return useMemo(
    () => (
      <>
        <GridProgress loading={delayedLoading} sx={{ pb: 5 }}>
          <Box sx={{ minWidth: 1050 }}>
            <TableContainer sx={{ height: 600 }}>
              <Table sx={{ minWidth: 750, height: 'max-content' }}>
                <TableHead sx={{ backgroundColor: CustomPalette.SecondaryCloud }}>
                  <TableRow sx={{ '& .MuiTableCell-root': { paddingLeft: 0, border: 'none' }, height: '48px' }}>
                    <TableCell sx={{ visibility: 'none', width: 0 }}></TableCell>
                    {props.titles?.map((title) => (
                      <TableCell key={title.toString()}>{title}</TableCell>
                    ))}
                    <TableCell sx={{ visibility: 'none', width: 0 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!list.items.length ? (
                    <TableNoDataRow colSpan={props.titles.length + 2} message={props.emptyList} />
                  ) : (
                    list?.items.map((course: Course) => (
                      <CourseListItem
                        key={course.id}
                        data={course}
                        objectType={course.code ? `${course.code}` : `${course.id}`}
                        routeLabel={props.routeLabel}
                        openUpdateMenu={props.openAction}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {Menus}
          <Box sx={{ display: 'flex', justifyContent: 'start', mt: 2, ml: 6 }}>
            <CustomPagination
              total={list.total}
              limit={size}
              page={page}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              handlePrefetch={prefetchNext}
            />
          </Box>
        </GridProgress>
      </>
    ),
    [delayedLoading, list, size, page],
  )
}
